import { useEffect, useState } from "react";
import { blogDataTypes } from "../utils/interfaces";
import { useParams } from "react-router";
import { blogsData } from "../utils/constants";
import { motion } from "framer-motion";
import { FiCalendar, FiArrowLeft } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import SEO from "./SEO";

const BlogDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Dynamic Data From API

  // const [data, setData] = useState<blogDataTypes | null>(null);
  // const [dataLoaded, setDataLoaded] = useState(false);

  // useEffect(() => {
  //   const getData = async () => {
  //     try {
  //       const response = await getBlogData();

  //       const [filterData] = response.filter(
  //         (blog: blogDataTypes) => blog._id === id
  //       );

  //       setData(filterData);
  //       setDataLoaded(true);
  //     } catch (error) {
  //       console.error("Error fetching blog data:", error);
  //     }
  //   };

  //   getData();
  // }, [id]);

  // static data

  const [data, setData] = useState<blogDataTypes | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    const filteredData = blogsData.filter((blog) => {
      const ID = +id!;
      return blog._id === ID;
    })[0];

    if (filteredData) {
      setData({
        _id: filteredData._id.toString(),
        image: filteredData.image,
        title: filteredData.title,
        description: filteredData.description,
        tags: filteredData.tags,
      });
      setDataLoaded(true);
    }
  }, [id]);

  // Extract plain text for SEO
  const stripHtml = (html: string) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const plainDescription = data ? stripHtml(data.description).substring(0, 160) : "";

  return (
    <>
      {dataLoaded && data && (
        <SEO
          title={`${data.title} | Nexasoft Infotech Blog`}
          description={plainDescription || `Read about ${data.title} on Nexasoft Infotech's blog. Expert insights on software development, web development, and technology trends.`}
          keywords={data.tags.join(", ")}
          image={data.image}
          url={`https://nexasoftinfotech.com/blog/${data._id}`}
          type="article"
          article={{
            publishedTime: new Date().toISOString(),
            modifiedTime: new Date().toISOString(),
            author: "Nexasoft Infotech",
            tags: data.tags,
          }}
        />
      )}
      <article className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
        {dataLoaded && data ? (
        <div className="relative px-p83 pb-10 pt-5 500:pt-8 min-h-screen overflow-hidden">
          {/* Background decorative elements */}
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary-500/8 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/2 pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary-500/8 rounded-full blur-3xl translate-y-1/2 translate-x-1/2 pointer-events-none" />

          <div className="relative z-10 max-w-4xl mx-auto">
            {/* Back Button */}
            <motion.button
              onClick={() => {
                navigate('/');
                // Scroll to blogs section after navigation
                setTimeout(() => {
                  const blogsSection = document.getElementById('blogs');
                  if (blogsSection) {
                    blogsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 300);
              }}
              className="flex items-center gap-2 text-neutral-300 hover:text-primary-400 font-poppins-medium mb-6 transition-colors duration-300 group"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <FiArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
              <span>Back to Blogs</span>
            </motion.button>

            {/* Title */}
            <motion.h1
              className="text-2xl 400:text-3xl 500:text-4xl 700:text-5xl font-merry-bold text-neutral-100 mb-4 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              {data.title}
            </motion.h1>

            {/* Date Badge */}
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-500/20 to-primary-600/10 border border-primary-500/30 rounded-full mb-6 backdrop-blur-sm"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <FiCalendar className="w-4 h-4 text-primary-400" />
              <span className="text-sm font-poppins-medium text-primary-300">
                Latest Article
              </span>
            </motion.div>

            {/* Image */}
            <motion.figure
              className="relative mb-8 rounded-2xl overflow-hidden shadow-2xl"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <img
                src={data.image}
                alt={data.title}
                className="w-full h-auto object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
            </motion.figure>

            {/* Content */}
            <motion.div
              className="bg-gradient-to-br from-neutral-800/95 via-neutral-800 to-neutral-900/95 rounded-3xl p-6 500:p-8 shadow-2xl border border-neutral-700/50 backdrop-blur-sm"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {/* Description */}
              <div
                className="blog-content max-w-none mb-6 text-neutral-300 font-poppins-regular leading-relaxed"
                dangerouslySetInnerHTML={{ __html: data.description || "" }}
              />

              {/* Tags */}
              <div className="flex flex-wrap gap-3 pt-6 border-t border-neutral-700/50">
                <span className="text-sm font-poppins-semi-bold text-neutral-400 mr-2">
                  Tags:
                </span>
                {data.tags.map((tag, i) => (
                  <motion.span
                    key={i}
                    className="px-4 py-2 bg-gradient-to-r from-primary-500/20 to-primary-600/10 border border-primary-500/30 text-primary-300 rounded-full text-sm font-poppins-medium backdrop-blur-sm"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.5 + i * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                  >
                    {tag}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500 mb-4"></div>
            <p className="text-neutral-300 font-poppins-medium">Loading...</p>
          </div>
        </div>
      )}
      </article>
      {dataLoaded && data && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BlogPosting",
              "headline": data.title,
              "description": plainDescription,
              "image": data.image,
              "author": {
                "@type": "Organization",
                "name": "Nexasoft Infotech",
                "url": "https://nexasoftinfotech.com"
              },
              "publisher": {
                "@type": "Organization",
                "name": "Nexasoft Infotech",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://nexasoftinfotech.com/logo.png"
                }
              },
              "datePublished": new Date().toISOString(),
              "dateModified": new Date().toISOString(),
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": `https://nexasoftinfotech.com/blog/${data._id}`
              },
              "keywords": data.tags.join(", ")
            })
          }}
        />
      )}
    </>
  );
};

export default BlogDetailPage;
